import { CATALOG_ITEM_TYPE, CatalogItemType } from '@pubstack/common/src/catalogItem'
import { MediaType } from '@pubstack/common/src/media-types'
import { PrebidModuleType } from '@pubstack/common/src/prebidModule'
import { SITE_ENV_LABELS, SiteEnv } from '@pubstack/common/src/site'
import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent, KeyboardEvent, MouseEvent, ReactNode, useId, useState } from 'react'
import { WithClassName } from '~/types/utils'
import { CatalogLogo } from './CatalogLogo'
import Button from '../Button'
import { Icon, IconName } from '../Icon'
import { TrackingLabel } from '../layout/ProductTrackingIntegration'
import { SITE_ENV_ICON_FILLED } from '../siteEnvIcons'

export const MODULE_CARD_WIDTH_PX = 286

const CATALOG_ITEM_TYPE_CONFIG: { [key in (typeof CATALOG_ITEM_TYPE)[CatalogItemType]]: { label: string; className: string } } = {
  prebidModule: { label: 'Prebid module', className: 'border-kaiminus/20 text-kaiminus' },
  prebidBidder: { label: 'Prebid bidder', className: 'border-ming/20 text-ming' },
  wrapper: { label: 'Wrapper', className: 'border-lava/20 text-lava' },
  adServer: { label: 'Ad server', className: 'border-violet/20 text-violet' },
  otherModules: { label: 'Other module', className: 'border-kaiminus/20 text-kaiminus' },
}

/** How a catalog entry can be integrated — same wording as the Type column of My bidder seats. */
const CARD_INTEGRATIONS = ['Client-side', 'Server-side', 'Spark for Pubs', 'Spark Demand'] as const
export type CardIntegration = (typeof CARD_INTEGRATIONS)[number]

/** Always listed, in this order, in the Platforms group. Spark Demand is the one exception — see below. */
/** Exported so the catalog's platform facet offers exactly the platforms the cards list, in the same order. */
export const CANONICAL_PLATFORM_INTEGRATIONS: readonly CardIntegration[] = ['Client-side', 'Server-side', 'Spark for Pubs']

/** Exported so the seat-type chips and the seat header draw an integration with the glyph its card uses. */
export const CARD_INTEGRATION_ICON: Record<CardIntegration, IconName> = {
  'Client-side': 'client',
  'Server-side': 'server',
  'Spark for Pubs': 'pubstack',
  // Pubstack-internal. Never carries `locked` — whoever can see it can create it.
  'Spark Demand': 'admin',
}

const MEDIA_TYPE_ORDER: readonly MediaType[] = ['banner', 'video', 'native']
const MEDIA_TYPE_ICON: Record<MediaType, IconName> = { banner: 'banner', video: 'video', native: 'native' }
const MEDIA_TYPE_LABEL: Record<MediaType, string> = { banner: 'Banner', video: 'Video', native: 'Native' }

/**
 * `s2s` has no entry of its own here — it is not a surface a publisher picks, it is how `web` gets
 * served. iOS and Android are listed separately despite sharing one underlying value (`inapp`): the
 * catalog has no way to tell them apart, but a single undifferentiated "mobile" entry would say less
 * than the design calls for, so both light up together off the same `inapp` support.
 */
const ENV_ORDER: readonly SiteEnv[] = ['web', 'ios', 'android', 'amp']

/**
 * Three states, not two. `locked` is a capability the bidder genuinely has and only this scope cannot
 * create — collapsing it into `unsupported` made the card state something false about the bidder, and
 * erased the cross-sell the rest of the catalog is built to carry.
 */
type CompatState = 'supported' | 'locked' | 'unsupported'

type CompatItem = { key: string; label: string; iconName: IconName; state: CompatState }

/** Word for word the Spark chip's string on this same card, because it is the same fact about the same scope. */
const compatStateName = (label: string, state: CompatState): string =>
  state === 'locked' ? `${label} — not available on this scope` : `${label}: ${state === 'supported' ? 'supported' : 'not supported'}`

/**
 * The visible half of the state, so it survives a copy-paste, a Ctrl-F and a greyscale screen — with
 * the glyph and the colour it was carried on three channels, none of them the text, and every card in
 * the catalog extracted to the same thirteen lines.
 *
 * Supported entries stay bare on purpose: most of the sheet is supported, and a suffix on every line
 * would bury the two states a reader is actually scanning for under the one they are not.
 */
const COMPAT_SUFFIX: Record<CompatState, string> = { supported: '', locked: ' (not on this scope)', unsupported: ' (not supported)' }

/**
 * One capability, named in full.
 *
 * Support is not carried by colour alone: an unsupported entry says so in its accessible name. The
 * colour-coded icon grid this replaced needed a tooltip per tile to be read at all, which put the only
 * copy of a bidder's compatibility behind a hover on a hover. A rule through the words made the list
 * look damaged rather than partly filled, and only cost legibility.
 *
 * `locked` keeps `text-ink`, since the bidder does support it and greying it out is the misstatement.
 * That makes locked and supported identical apart from the padlock, so the glyph is load-bearing
 * rather than decorative — it and the accessible name are the whole non-colour channel. The padlock
 * stays Lava-free on purpose: in this app that colour means "Pubstack-internal", not "not yours".
 */
const CompatEntry: FunctionComponent<CompatItem> = ({ label, iconName, state }) => (
  <li
    aria-label={compatStateName(label, state)}
    /* `text-hurricane` rather than `text-silver`: silver is 1.74:1 on white and 1.66:1 on a dimmed
       card's `bg-cloud`, well under WCAG's 4.5:1 for 11px text — and these greyed entries are exactly
       what a reader scans to rule a bidder out. Hurricane clears it on both grounds (7.18 / 6.48) and
       still reads as plainly de-emphasised against `text-ink`. Check any replacement against
       *both* backgrounds: `slate-grey` and `ash` pass on white and fail on `bg-cloud`. */
    className={cn('flex items-center gap-0.5 text-[11px] leading-5', state === 'unsupported' ? 'text-hurricane' : 'text-ink')}
  >
    <Icon width={'12px'} height={'12px'} name={state === 'locked' ? 'lock' : iconName} />
    <span>
      {label}
      {COMPAT_SUFFIX[state]}
    </span>
  </li>
)

const CompatGroup: FunctionComponent<{ title: string; items: CompatItem[] }> = ({ title, items }) => (
  <div className={'flex flex-col'}>
    <div className={'text-[10px] leading-4 font-medium tracking-wide text-slate-grey uppercase'}>{title}</div>
    <ul className={'flex flex-wrap gap-x-2'}>
      {items.map((item) => (
        <CompatEntry {...item} key={item.key} />
      ))}
    </ul>
  </div>
)

const platformItems = (integrations: CardIntegration[], lockedIntegrations?: CardIntegration[]): CompatItem[] => {
  const canonical = CANONICAL_PLATFORM_INTEGRATIONS.map((integration) => ({
    key: integration,
    label: integration,
    iconName: CARD_INTEGRATION_ICON[integration],
    /* `lockedIntegrations` is built as `integrations.filter((i) => !available.has(i))`, so it is by
       construction a subset: locked always means the bidder does support this and only the scope
       cannot create it. Hence the order — absence is the only thing that makes a platform unsupported. */
    state: !integrations.includes(integration) ? 'unsupported' : lockedIntegrations?.includes(integration) ? 'locked' : 'supported',
  })) satisfies CompatItem[]
  // Nothing in the catalog produces this today; the card keeps the capability for whatever renders
  // internal seats next.
  return integrations.includes('Spark Demand') ? [...canonical, { key: 'Spark Demand', label: 'Spark Demand', iconName: CARD_INTEGRATION_ICON['Spark Demand'], state: 'supported' }] : canonical
}

export type PureModuleCardProps = WithClassName & {
  baseCDNUrl: string
  type: CatalogItemType
  moduleType?: PrebidModuleType
  code: string
  name: string
  topLeftIcon?: ReactNode
  topRightIcon?: ReactNode
  footerIcon: ReactNode
  status?: ReactNode
  /** One line on what the company does. Omitted where the catalog has none — the row simply collapses. */
  description?: string
  /** Listed under Media types when given; omitted on catalogs with no media-type concept of their own. */
  mediaTypes?: MediaType[]
  /** Listed under Environments when given; omitted on catalogs with no site-env concept of their own. */
  envs?: SiteEnv[]
  /**
   * Listed under Platforms when given — every canonical platform is named, marked supported when the
   * entry offers it and this scope can create it. Falls back to `moduleType` alone when given, else the
   * item type ("Prebid module", "Wrapper", …), on catalogs with no platform concept of their own at all.
   */
  integrations?: CardIntegration[]
  /** The subset of `integrations` this scope cannot create: named as unsupported rather than dropped. */
  lockedIntegrations?: CardIntegration[]
  /** The subset of `integrations` open to Pubstack staff only on this scope (user access off): marked with a shield. Never also locked. */
  staffOnlyIntegrations?: CardIntegration[]
  /**
   * Names what clicking the card does ("Add seat", "Upgrade"), in the footer, whichever face is showing.
   * Cards that are only ever clicked as a whole leave it out and say nothing.
   */
  actionLabel?: string
  /**
   * How the footer's action reads. `link` is the default — the label as text or as a bare button, the
   * quiet form that suits a grid where every card offers something.
   *
   * `cta` makes it a filled primary button, for a card being *promoted* rather than merely listed: on the
   * Featured row the offer is the reason the card is there, and a text link is not what asks for it.
   * Ignored without a `trackingLabel`, which the button requires.
   */
  actionVariant?: 'link' | 'cta'
  /** Out of reach on this scope — greyed out, still clickable, because the click is how it gets bought. */
  dimmed?: boolean
  /**
   * The act is not this viewer's to perform — a `user` where the seat is an owner's call.
   *
   * Distinct from `dimmed`, which means the scope has not bought the thing and whose click is how it
   * gets bought. Here the entry is owned and the card is worth reading; it is the one gesture that is
   * refused, so the footer greys and stops responding while the card itself stays legible. The reason
   * belongs on the footer icon's tooltip, which is not disabled and so can still be hovered.
   */
  actionDisabled?: boolean
  trackingLabel?: TrackingLabel
  onClick?: () => void
}

/** Inner controls do their own thing; the card's own click must not fire underneath them. */
const swallow = (handler?: () => void) => (event: MouseEvent) => {
  event.stopPropagation()
  handler?.()
}

/**
 * One catalog entry: who this is, what they do, and the one thing to do about it.
 *
 * Every card is the same height whatever it has to say, because the two things it can show — the pitch
 * and the compatibility — are layers stacked in one fixed box that cross-fade, rather than content that
 * pushes the card taller when it opens. A grid that reflowed under the pointer would move the very card
 * being read out from under it.
 *
 * The compatibility is behind one named control rather than behind the card itself: pointing at a card to
 * read who it is used to flip it to a spec sheet, which made the pitch unreadable by the very gesture used
 * to reach it. Hovering "More" opens the layer, clicking it pins it — a hover is unreachable by keyboard
 * and does not exist on touch, so the pin is what makes the layer readable at all there, and clicking the
 * layer is what puts it away again once the control that opened it has stepped aside.
 */
export const ModuleCard: FunctionComponent<PureModuleCardProps> = ({
  className,
  baseCDNUrl,
  type,
  moduleType,
  code,
  name,
  topLeftIcon,
  topRightIcon,
  footerIcon,
  status,
  description,
  mediaTypes,
  envs,
  integrations,
  lockedIntegrations,
  staffOnlyIntegrations,
  actionLabel,
  actionVariant = 'link',
  dimmed,
  actionDisabled,
  trackingLabel,
  onClick,
}) => {
  const [pinned, setPinned] = useState(false)
  const [detailHovered, setDetailHovered] = useState(false)
  const panelId = useId()

  /* One place the refusal is applied, rather than at each of the four things that can trigger the act —
     the card, its keyboard equivalent, the footer button and the compatibility sheet. */
  const act = actionDisabled ? undefined : onClick

  // A card with nothing to say about compatibility has no second layer, and stays the plain card it is
  // on Modules and the Marketplace.
  const hasCompatibility = !!(mediaTypes || envs || integrations)
  const showDetail = hasCompatibility && (pinned || detailHovered)

  /* Greying is pinned off while the detail is showing: it is the same gesture that reveals the layer, so
     a card that stayed grey while displaying its spec sheet would be answering two questions at once. */
  const dim = !!dimmed && !showDetail
  const dimmable = cn(
    'transition-[filter,opacity] duration-500 ease-out',
    /* Held back from a full desaturation: at `opacity-55 grayscale` the logo and the name went so flat that
       the card read as broken rather than as out of reach, and the thing being offered stopped being legible
       enough to want. Dimmed is a state to notice, not a state to squint at. */
    dim && 'opacity-75 grayscale-50 group-hover:opacity-100 group-hover:grayscale-0 group-focus-within:opacity-100 group-focus-within:grayscale-0'
  )

  /** A promoted card's filled CTA — a real button, so it counts as an inner control below. */
  const isCtaAction = actionVariant === 'cta' && !!actionLabel && !!trackingLabel

  /* Real buttons cannot be nested in another button, so the card gives up its own button role to the one
     control it can grow — the compatibility trigger — and the footer's action label steps up from text to
     a real button in its place, so the act stays reachable by keyboard. Wherever the card can stay the
     control, it does, and the footer stays the plain text naming it. */
  const hasInnerControls = hasCompatibility || isCtaAction

  const supportsSparkForPubs = !!integrations?.includes('Spark for Pubs')
  const sparkForPubsLocked = !!lockedIntegrations?.includes('Spark for Pubs')
  const sparkForPubsStaffOnly = !sparkForPubsLocked && !!staffOnlyIntegrations?.includes('Spark for Pubs')

  return (
    <div
      className={cn(
        /* `isolate` is load-bearing: the sheet and the trigger are positioned layers, and a `relative` box
           with no z-index of its own does not contain them. Without it their z-indexes are compared against
           everything else on the page, and the card's own furniture paints straight through an open flyout. */
        'group relative isolate flex w-[286px] flex-col overflow-hidden rounded-md border transition-[transform,border-color,background-color,box-shadow] duration-700 ease-out hover:-translate-y-1 hover:scale-[1.015] hover:border-silver hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-king',
        /* The card still lifts on hover when its act is refused — it is a card worth reading, and the
           compatibility sheet inside it is still worth opening. Only the pointer stops promising. */
        actionDisabled ? 'cursor-default' : 'cursor-pointer',
        /* Out of reach reads as a flat grey tile rather than as a bordered card: the outline is what makes
           the others look like things to pick up, and drawing one around what cannot be picked up is the
           part that misleads. Both come back on hover, with the colour — the card is still clickable, and
           that click is how the offer gets asked for. */
        dimmed ? 'border-transparent bg-cloud hover:bg-white' : 'border-platinum bg-white',
        className
      )}
      data-tracking={trackingLabel}
      data-dimmed={dim || undefined}
      onClick={act}
      {...(hasInnerControls
        ? {}
        : {
            role: 'button',
            tabIndex: 0,
            onKeyDown: (event: KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                act?.()
              }
            },
          })}
    >
      <div className={cn('flex flex-col p-3', dimmable)} aria-hidden={showDetail}>
        {/* Fixed, so the card is the same height whatever it has to say and whichever face is showing. */}
        <div className={'flex h-[188px] flex-col gap-2'}>
          <div className={'flex flex-col gap-2'}>
            <div className={'flex items-start gap-2'}>
              <div className={'flex size-[66px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-platinum'}>
                <CatalogLogo baseCDNUrl={baseCDNUrl} code={code} name={name} />
              </div>
              <div className={'flex min-w-0 flex-1 flex-col gap-1'}>
                <div className={'flex min-w-0 items-center gap-1'}>
                  {/* Titled because it truncates: "Equativ (formerly Smart AdServer)" renders as
                      "Equativ (formerly Smar…" with no way to read, hover, or widen it. */}
                  <div className={'truncate text-base font-medium text-ink'} title={name}>
                    {name}
                  </div>
                  {topLeftIcon}
                  <div className={'flex-1'} />
                  {topRightIcon}
                </div>
                {/* The one badge worth carrying on the resting card: Spark is what the catalog is selling,
                    and whether this scope can switch it on is the question a reader opens the page with.
                    Its grey is its own — a hue would come back with the card on hover and read as
                    available. */}
                {supportsSparkForPubs && (
                  <span
                    className={cn(
                      'inline-flex w-fit items-center gap-1 rounded-full border px-1.5 text-xs whitespace-nowrap',
                      sparkForPubsLocked ? 'border-platinum text-slate-grey' : sparkForPubsStaffOnly ? 'border-king text-king' : 'border-milka/20 text-milka'
                    )}
                    title={sparkForPubsLocked ? 'Spark for Publishers — not available on this scope' : sparkForPubsStaffOnly ? 'Spark for Publishers (Pubstack staff only)' : 'Spark for Publishers'}
                  >
                    <Icon width={'12px'} height={'12px'} name={sparkForPubsStaffOnly ? 'admin' : 'pubstack'} />
                    <span>{'Spark for Pubs'}</span>
                  </span>
                )}
                {/* No platform to speak of at all: the module type, or failing that the item type, is the
                    only thing left to say. */}
                {!integrations && (
                  <span className={cn('inline-flex w-fit items-center rounded-full border px-1.5 text-xs whitespace-nowrap', CATALOG_ITEM_TYPE_CONFIG[type].className)}>
                    {moduleType ?? CATALOG_ITEM_TYPE_CONFIG[type].label}
                  </span>
                )}
              </div>
            </div>
            {description && <p className={'line-clamp-3 text-sm text-ash'}>{description}</p>}
          </div>
        </div>
      </div>

      {/* Never covered by the sheet: the act keeps one place on the card, so opening the compatibility does
          not move the button the reader is about to press, nor make them learn a second one. */}
      <div className={cn('relative z-20 flex h-11 items-center gap-2 border-t border-platinum px-3', dimmable)}>
        {actionLabel ? (
          isCtaAction ? (
            <span className={'flex min-w-0 items-center gap-2'}>
              {footerIcon}
              <Button size={'s'} variant={'primary'} disabled={actionDisabled} onClick={swallow(act)} trackingLabel={trackingLabel}>
                {actionLabel}
              </Button>
            </span>
          ) : hasInnerControls ? (
            <button
              type={'button'}
              /* Not the DOM `disabled`: that would take the footer icon out of the hover chain with it,
                 and the icon's tooltip is where the reason lives. It is inert by having no handler and
                 says so by its colour and cursor. */
              aria-disabled={actionDisabled}
              onClick={swallow(act)}
              className={cn(
                'flex min-w-0 items-center gap-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-king',
                actionDisabled ? 'cursor-not-allowed text-slate-grey' : 'cursor-pointer',
                !actionDisabled && (dimmed ? 'text-slate-grey' : 'text-king')
              )}
            >
              {footerIcon}
              {/* Sized on the span rather than the button, for the same Emotion-reset reason as "More" below. */}
              <span className={'truncate text-sm font-medium'}>{actionLabel}</span>
            </button>
          ) : (
            <span className={cn('flex min-w-0 items-center gap-1 text-sm font-medium', actionDisabled || dimmed ? 'text-slate-grey' : 'text-king')}>
              {footerIcon}
              <span className={'truncate'}>{actionLabel}</span>
            </span>
          )
        ) : (
          <span className={'flex min-w-0 items-center gap-1'}>{footerIcon}</span>
        )}
        <div className={'flex-1'} />
        <span className={'shrink-0'}>{status}</span>
      </div>

      {/* One hover region for the trigger and the sheet it opens, so that moving off the trigger and onto
          the sheet is not a departure. `display: contents` keeps both positioned against the card. */}
      {hasCompatibility && (
        <div className={'contents'} onMouseEnter={() => setDetailHovered(true)} onMouseLeave={() => setDetailHovered(false)}>
          {/* The spec sheet takes the card down to its footer: it is the longest thing the card has to say,
              and the face it covers is the one naming what the card is. The footer is what it stops short
              of — that row is the act, and the act does not change with the face. */}
          <div
            id={panelId}
            aria-hidden={!showDetail}
            /* The sheet is a face of the card, not a thing of its own, so a tap on it is a tap on the card:
               it does what the footer says — add a seat, or ask for the upgrade — rather than merely closing
               back to the face that says the same. The sheet is unpinned first, so the card is resting again
               behind whatever the act opens, and `More` is there to reopen it. */
            onClick={swallow(() => {
              setPinned(false)
              act?.()
            })}
            className={cn(
              'absolute inset-x-0 top-0 bottom-11 z-10 flex flex-col gap-2 bg-white p-3 transition-opacity duration-500 ease-out',
              showDetail ? 'opacity-100' : 'pointer-events-none opacity-0'
            )}
          >
            {/* Whose spec sheet this is. The sheet covers the only thing carrying the name, and a grid of
                cards that all open into the same unlabelled list of capabilities is one the reader has to
                close again to know what they were just reading. */}
            <div className={'flex shrink-0 items-center gap-2'}>
              <div className={'flex size-6 shrink-0 items-center justify-center overflow-hidden rounded border border-platinum'}>
                {/* Sized down to the tile: the card's own logo is drawn for a 66px square, and both the
                    wordmark's max bounds and the monogram's type would overflow a 24px one. */}
                <CatalogLogo className={'max-h-full max-w-full text-[10px]'} baseCDNUrl={baseCDNUrl} code={code} name={name} />
              </div>
              <div className={'truncate text-sm font-medium text-ink'} title={name}>
                {name}
              </div>
            </div>

            {/* Scrolls rather than pushes: the card is a fixed box, and a bidder listing every platform
                must not make the grid reflow under the pointer reading it. */}
            <div className={'flex flex-1 flex-col justify-center gap-2 overflow-y-auto'}>
              {mediaTypes && (
                <CompatGroup
                  title={'Media types'}
                  /* Media types and environments have no locked state — nothing gates them per scope —
                     so they stay a straight two-way split off the same predicate as before. */
                  items={MEDIA_TYPE_ORDER.map((mediaType) => ({
                    key: mediaType,
                    label: MEDIA_TYPE_LABEL[mediaType],
                    iconName: MEDIA_TYPE_ICON[mediaType],
                    state: mediaTypes.includes(mediaType) ? 'supported' : 'unsupported',
                  }))}
                />
              )}
              {envs && (
                <CompatGroup
                  title={'Environments'}
                  items={ENV_ORDER.map((env) => ({ key: env, label: SITE_ENV_LABELS[env], iconName: SITE_ENV_ICON_FILLED[env], state: envs.includes(env) ? 'supported' : 'unsupported' }))}
                />
              )}
              {integrations && <CompatGroup title={'Platforms'} items={platformItems(integrations, lockedIntegrations)} />}
            </div>
          </div>

          {/* Named rather than iconographic, and the only thing that opens the sheet. It steps aside once
              the sheet is up — it is a way in, and the sheet it opened is not the place to keep offering
              it. It stays in place rather than unmounting, so the pointer resting on it lands on the sheet
              instead of on nothing, and stays reachable by keyboard, which is the whole of the route in
              where there is no hover at all. */}
          <button
            type={'button'}
            /* "More" alone is only legible next to the card it sits on; the label spells out what more there
               is, and still opens with the visible word so a voice command for it lands. */
            aria-label={'More compatibility details'}
            aria-expanded={showDetail}
            aria-controls={panelId}
            data-hidden={showDetail || undefined}
            onClick={swallow(() => setPinned((open) => !open))}
            className={cn(
              'absolute right-3 bottom-12 z-20 inline-flex cursor-pointer items-center gap-0.5 whitespace-nowrap underline-offset-2 transition-opacity duration-300 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-king',
              showDetail ? 'pointer-events-none text-king opacity-0' : 'text-slate-grey hover:text-king hover:underline'
            )}
          >
            {/* Sized on the span rather than the button: the Emotion global reset reaches `button` unlayered,
                and an unlayered rule outranks every Tailwind utility whatever its specificity, so `text-sm`
                on the button itself is overridden by `font: inherit` and the word comes out at the card's
                own 16px. Set to the seat count's type — the two sit a line apart in the same corner, and a
                control at a different size from the label beside it reads as belonging to something else. */}
            <span className={'text-sm/[22px]'}>{'More'}</span>
            <Icon width={'12px'} height={'12px'} name={showDetail ? 'chevron_up' : 'chevron_down'} />
          </button>
        </div>
      )}
    </div>
  )
}
